import moment from 'moment';
import 'moment/locale/pt-br';

import { mapState } from 'vuex';

import BANKS from '@sdk/helpers/domains/banks';
import namelize from '@sdk/helpers/formatters/namelize';
import patternize from '@sdk/helpers/formatters/patternize';

import { client as pusherClient, uploadChannel } from '@sdk/helpers/misc/pusher';

import { DATE_FORMAT, DATE_FORMAT_CHARS } from '@cordel/helpers/form/validators/date';
import { PHONE_FORMAT } from '@cordel/helpers/form/validators/phone';
import { CELLPHONE_FORMAT } from '@cordel/helpers/form/validators/cellphone';

import preload from '@/helpers/mixins/preload';

import { subtractDate } from '@/helpers/filters/date';

import dataHelper from '@/_CRUDL/helpers/data/items';

import customerMap, {
  personalReferenceMap,
  partnersMap,
  bankReferenceMap,
  jobReferenceMap,
  previousJobReferenceMap,
  accountantMap,
  emailMap,
  // spouseMap,
  cellPhoneMap,
  mainPhoneMap,
  // nationalityMap,
  // cnhMap,
  mainAddressMap,
  previousAddressMap,
  alternativeAddressMap,
  jobReferenceAddressMap,
  domainsMap,
  companyMap,
  attachmentMaps, // ARRAY!
} from '@/views/Customers/Form/maps';

import domains from '@/helpers/apis/domains';

export default {
  mixins: [
    // former,
    preload,
  ],
  computed: {
    ...mapState([
      'attachments',
      'customers',
      'domainsWeb',
    ]),
  },
  data() {
    return {
      channel: null,
    };
  },
  mounted() {
    this.getAttachments();
  },
  beforeDestroy() {
    if (this.clearStores) {
      this.clearStores();
    }

    this.unsubscribeUploads();
  },
  methods: {
    getBanksWeb() {
      [
        customerMap.bank_references[0].bank_id,
        customerMap.previous_funding_bank_id,
      ].forEach(id => this.setFieldProcessing(id, true));

      return this
        .preloadBanksWeb(true, { main: true })
        .then(this.gotBanksWeb);
    },

    gotBanksWeb() {
      [
        customerMap.bank_references[0].bank_id,
        customerMap.previous_funding_bank_id,
      ].forEach((id) => {
        this.setFieldOptions(
          id,
          dataHelper(this.banksWeb.list.items),
          'name',
          'id',
        );

        this.setFieldProcessing(id, false);
      });
    },

    getDomainsWeb() {
      return this.preloadDomainsWeb().then(this.gotDomainsWeb);
    },

    gotDomainsWeb() {
      const process = (id, items) => {
        if (this.getField(id) && Array.isArray(items)) {
          let options = items;

          const {
            include,
            exclude,
          } = this.getField(id);

          if (include) {
            options = items.filter(o => include.indexOf(o.id) > -1);
          }

          if (exclude) {
            options = items.filter(o => exclude.indexOf(o.id) === -1);
          }

          this.setFieldOptions(
            id,
            dataHelper(options),
            'name',
            'id',
            undefined,
            id !== customerMap.property ? undefined : (a, b) => a.value - b.value,
          );
        }
      };

      [
        mainAddressMap.build_type_id,
        previousAddressMap.build_type_id,
        alternativeAddressMap.build_type_id,
      ].forEach(id => process(id, this.domainsWeb.list.items.build_type));

      [
        customerMap.state_of_birth_id,
        customerMap.rg_state_id,
        mainAddressMap.state_id,
        previousAddressMap.state_id,
        alternativeAddressMap.state_id,
        jobReferenceAddressMap.state_id,
      ].forEach(id => process(id, this.domainsWeb.list.items.states));

      let filterRG = false;
      let filterCTPS = false;

      try {
        filterRG = this.$route.query.bank_code.split(',').includes(BANKS.BV.code)
                    || this.$route.query.bank_code.split(',').includes(BANKS.BRADESCO.code)
                    || this.$route.query.bank_code.split(',').includes(BANKS.PAN.code);
        filterCTPS = this.$route.query.bank_code.split(',').includes(BANKS.C6.code);
      } catch (err) { /* */ }

      Object.keys(domainsMap).forEach((id) => {
        const key = domainsMap[id];
        const options = this.domainsWeb.list.items[id];

        if (typeof key === 'string') {
          if (filterRG && key === 'customerDocumentType') {
            process(key, [
              Object
                .values(options)
                .find(d => d.id === 'RG'),
            ]);
          } else if (filterCTPS && key === 'customerDocumentType') {
            process(key, options
              .filter(i => i.id !== 'CTPS'));
          } else {
            process(key, options);
          }
        } else {
          key.forEach(k => process(k, options));
        }
      });
    },

    getAttachments() {
      this.$store
        .dispatch('attachments/token')
        .then(this.gotAttachments);
    },

    gotAttachments() {
      const pusher = pusherClient();

      if (!pusher) {
        return;
      }

      // Nem todo chamador cunha token novo: o setSections da troca PF↔PJ
      // (views/Customers/Form.vue) e o setSection do perfil
      // (views/Customers/Read/Profile.vue) reentram aqui com a MESMA chave. Aí
      // subscribe devolve o canal que já está assinado e o bind vira o segundo —
      // um upload só rodaria o handler duas vezes. Soltar o anterior primeiro
      // deixa o par bind/unbind 1:1 venha de onde vier a chamada.
      this.unsubscribeUploads();

      const { key } = this.attachments.result;

      this.channel = uploadChannel(key);

      pusher.subscribe(this.channel).bind('uploaded', this.gotNewAttachment);
    },

    unsubscribeUploads() {
      const pusher = pusherClient();

      if (!pusher || !this.channel) {
        return;
      }

      const channel = pusher.channel(this.channel);

      if (channel) {
        channel.unbind('uploaded', this.gotNewAttachment);
      }

      // Quem tira o canal do registro é o CLIENTE: Channel#unsubscribe não recebe
      // nome e não desfaz a inscrição. E o cliente aqui é o do host, que
      // sobrevive à navegação da SPA — canal não removido é re-inscrito a cada
      // reconnect. O nome vem do subscribe, não de attachments.result.key, que a
      // esta altura já pode ser outro.
      pusher.unsubscribe(this.channel);
      this.channel = null;
    },

    gotNewAttachment(data) {
      const { type } = data;

      const keys = attachmentMaps
        .concat(customerMap.cnh_ocr_file)
        .filter(id => this.getField(id).meta.type === type);

      if (!keys.length) {
        return;
      }

      keys.map(key => this.updateAttachmentList(key, type));

      keys.map(key => this.setAttachmentFieldValue(key, {
        original_filename: data.filename,
        remote_file_id: data.key,
      }));
    },

    setAttachmentsList() {
      this.$store.dispatch('attachments/update', {
        list: attachmentMaps.reduce((list, key) => {
          const field = this.getField(key);

          if (!field) {
            return list;
          }

          if (field.value) {
            list.push(field.meta.type);
          }

          return list;
        }, []),
      });
    },

    updateAttachmentList(key, type) {
      const {
        list,
      } = this.attachments;

      if (list.indexOf(key) === -1) {
        this.$store.dispatch('attachments/update', {
          list: [
            ...list,
            type,
          ],
        });
      }
    },

    setValue(id, value = '', options = {}) {
      let formatted = [undefined, null].indexOf(value) > -1 ? '' : value;
      const field = this.getField(id);

      if (!field) {
        return;
      }

      if (formatted) {
        switch (field.mask && field.mask.type) {
          case 'patternize':
            if (field.mask.format === DATE_FORMAT) {
              formatted = moment(formatted).format(DATE_FORMAT_CHARS);
            } else {
              formatted = patternize(formatted, field.mask);
            }

            break;
          default:
            break;
        }
      }

      // if (field.type === 'boolean') {
      //   console.log(formatted);
      // }

      this.setFieldValue(id, formatted, options);
    },

    setValues() {
      const {
        customer,
        setValue,
      } = this;

      if (!customer.id) {
        return;
      }

      const domainsWeb = this.domainsWeb.list.items;

      const { CELULAR, PRINCIPAL } = domains.phone_types;
      const { ANTERIOR, ALTERNATIVO } = domains.address_types;

      const {
        RG_FRONT,
        RG_BACK,
        CNH,
        CTPS,
        ADDRESS,
        INCOME,
      } = domains.person_attachment_types;

      const {
        FAT,
        CNPJ,
        CS,
      } = domains.attachment_types;

      const cpfCnpj = customer.cpf || customer.cnpj;

      const skip = [
        'INTERNAL_customer_type',
        'name',
        'cpf_cnpj',
        'cpf',
        'cnpj',
        'emails',
        'monthly_billings',
        'personal_references',
        'partners',
        'phones',
        'job_reference',
        'accountant',
        'address',
        'addresses',
        'bank_references',
        'nationality',
        'has_cnh',
      ];

      let filterRG = false;

      try {
        filterRG = this.$route.query.bank_code.split(',').includes(BANKS.BV.code)
                    || this.$route.query.bank_code.split(',').includes(BANKS.BRADESCO.code)
                    || this.$route.query.bank_code.split(',').includes(BANKS.PAN.code);
      } catch (err) { /* */ }

      if (filterRG && customer.document_type !== 'RG') {
        skip.push('document_type', 'rg', 'rg_issuing', 'rg_state_id', 'rg_date');

        this.setFieldValue(customerMap.document_type, 'RG');
      }

      Object.keys(customerMap)
        // .filter(key => !key.match(/^INTERNAL_/))
        .filter(key => skip.indexOf(key) === -1)
        .forEach(key => setValue(customerMap[key], customer[key]));

      this.setFieldSkip(customerMap.INTERNAL_customer_type, true);
      setValue(customerMap.INTERNAL_customer_type, !!customer.cpf, {
        silent: true,
      });

      setValue(customerMap.cpf_cnpj, cpfCnpj, { silent: true });
      setValue(customerMap.name, namelize(customer.name));

      if (customer.cpf) {
        // eslint-disable-next-line
        setValue(customerMap.has_cnh, customer.has_cnh === null ? (customer.cnh ? true : null) : customer.has_cnh);

        setValue(customerMap.nationality, customer.nationality && customer.nationality === 'Brasileira');
      }

      if (customer.emails && customer.emails.length) {
        setValue(emailMap.address, customer.emails[0].address);
      }

      if (customer.monthly_billings && customer.monthly_billings.length) {
        const list = customer.monthly_billings.reverse();

        Object.keys(companyMap.monthly_billings)
          .forEach((key) => {
            setValue(
              companyMap.monthly_billings[key],
              list[key].billing_cents,
            );
          });
      }

      this.setAddressFieldsValue(mainAddressMap, customer.address);

      if (customer.accountant) {
        Object.keys(accountantMap)
          .filter(key => key !== 'phone')
          .forEach(key => setValue(accountantMap[key], customer.accountant[key]));

        this.setPhoneFieldValue(accountantMap.phone, customer.accountant.phone);
      }

      if (customer.bank_references && customer.bank_references.length) {
        const firstBankReference = customer.bank_references[0];

        Object.keys(bankReferenceMap)
          .filter(key => ['account_number', 'bank_id'].indexOf(key) === -1)
          .forEach(key => setValue(bankReferenceMap[key], firstBankReference[key]));

        const {
          account_number: accountNumber,
          digit: accountDigit,
        } = firstBankReference;

        setValue(bankReferenceMap.bank_id, firstBankReference.bank.id);
        setValue(bankReferenceMap.account_number, [accountNumber, accountDigit].join('-'));
      }

      if (customer.partners && customer.partners.length) {
        const firstPartner = customer.partners[0];

        setValue(partnersMap.cpf_cnpj, firstPartner.cpf || firstPartner.cnpj);
        setValue(partnersMap.quota, parseFloat(firstPartner.quota * 100));

        [
          'name',
          'born_at',

          'started_at',

          'income_cents',

          'spouse_name',
          'spouse_born_at',
          'spouse_cpf',
        ].forEach(i => setValue(partnersMap[i], firstPartner[i]));

        const role = domainsWeb.partner_role
          .find(item => item.name === firstPartner.role);

        if (role && role.name) {
          this.setValue(partnersMap.role, role.id);
        } else if (this.sections[0].editable === false) { // yup. tests for false, not falsy!
          this.setValue(partnersMap.role, firstPartner.role);
        }

        const maritalStatusID = firstPartner.marital_status_id
          || (firstPartner.marital_status && firstPartner.marital_status.id);

        const maritalStatus = domainsWeb.marital_status
          .find(item => item.id === maritalStatusID);

        if (maritalStatus && maritalStatus.name) {
          this.setValue(partnersMap.marital_status_id, maritalStatusID);
        }
      }

      if (customer.personal_references && customer.personal_references.length) {
        const firstPersonalReference = customer.personal_references[0];

        Object.keys(personalReferenceMap)
          .filter(key => ['phone', 'relationship'].indexOf(key) === -1)
          .forEach(key => setValue(personalReferenceMap[key], firstPersonalReference[key]));

        const relation = domainsWeb.relationship
          .find(item => item.name === firstPersonalReference.relationship);

        if (relation && relation.name) {
          this.setValue(personalReferenceMap.relationship, relation.id);
        } else if (this.sections[0].editable === false) { // yup. tests for false, not falsy!
          this.setValue(personalReferenceMap.relationship, firstPersonalReference.relationship);
        }

        this.setPhoneFieldValue(personalReferenceMap.phone, firstPersonalReference.phone);
      }

      if (customer.cpf && customer.job_reference) {
        const jobReferenceIDs = [
          'another_income_type',
          'professional_ocupation',
          'profession',
          'company_activity',
          'phone',
        ];

        jobReferenceIDs
          .forEach((key) => {
            const id = jobReferenceMap[`${key}_id`];
            const option = customer.job_reference[key];

            if (!option || !this.getField(id)) {
              return;
            }

            setValue(id, option.id);
          });

        Object.keys(jobReferenceMap)
          .filter(key => jobReferenceIDs.indexOf(key.replace(/_id$/, '')) === -1)
          .filter(key => key !== 'first_job')
          .forEach(key => setValue(jobReferenceMap[key], customer.job_reference[key]));

        setValue(
          jobReferenceMap.first_job,
          customer.job_reference.first_job === null ? true : customer.job_reference.first_job,
        );

        if (customer.job_reference.address) {
          this.setAddressFieldsValue(jobReferenceAddressMap, customer.job_reference.address);
        }

        if (customer.job_reference.phone) {
          this.setPhoneFieldValue(jobReferenceMap.phone, customer.job_reference.phone);
        }

        Object.keys(previousJobReferenceMap)
          .filter(key => key !== 'previous_job_phone')
          .forEach(key => setValue(previousJobReferenceMap[key], customer.job_reference[key]));

        this.setPhoneFieldValue(
          previousJobReferenceMap.previous_job_phone,
          customer.job_reference.previous_job_phone,
        );

        const {
          label: occupation,
        } = this.getFieldOption(jobReferenceMap.professional_ocupation_id);

        const {
          label: profession,
        } = this.getFieldOption(jobReferenceMap.profession_id);

        const isAposentado = occupation === domains.ocuppations.APOSENTADO_PENSIONISTA
                             && profession === domains.professions.APOSENTADO;

        setValue(jobReferenceMap.INTERNAL_still_works, !isAposentado);
      }

      this.setPhoneFieldValue(
        cellPhoneMap.number,
        customer.phones.find(phone => phone.phone_type.name === CELULAR),
        cellPhoneMap.phone_confirmation_id,
      );

      this.setPhoneFieldValue(
        mainPhoneMap.number,
        customer.phones.find(phone => phone.phone_type.name === PRINCIPAL),
        mainPhoneMap.phone_confirmation_id,
      );

      this.setAddressFieldsValue(
        previousAddressMap,
        customer.addresses.find(a => a.address_type.name === ANTERIOR),
      );

      this.setAddressFieldsValue(
        alternativeAddressMap,
        customer.addresses.find(a => a.address_type.name === ALTERNATIVO),
      );

      if (customer.attachments && customer.attachments.length) {
        const findPerson = type => domainsWeb.person_attachment_type
          .find(t => t.identifier === type);

        const find = type => domainsWeb.attachment_type
          .find(t => t.identifier === type);

        let fileTypes = {
          [customerMap.address.address_file]: ADDRESS.type,
        };

        if (customer.cpf) {
          fileTypes = {
            ...fileTypes,
            [customerMap.ctps_file]: CTPS.type,
            [customerMap.rg_front_file]: RG_FRONT.type,
            [customerMap.rg_back_file]: RG_BACK.type,
            [customerMap.cnh_file]: CNH.type,

            [customerMap.job_reference.job_file]: INCOME.type,
          };
        } else {
          fileTypes = {
            ...fileTypes,
            [customerMap.cnpj_file]: CNPJ.type,
            [customerMap.cs_file]: CS.type,
            [customerMap.fat_file]: FAT.type,
          };
        }

        Object.keys(fileTypes).forEach((key) => {
          this.setAttachmentFieldValue(
            key,
            customer.attachments
              .find(a => a.attachment_type_id === {
                ...(findPerson(fileTypes[key]) || find(fileTypes[key])),
              }.id),
          );
        });
      }
    },

    setAttachmentFieldValue(id, item) {
      if (!item) {
        return;
      }

      const payload = {
        name: item.original_filename,
        key: item.remote_file_id,
      };

      if (item.id) {
        payload.id = item.id;
      }

      this.setFieldValue(id, JSON.stringify(payload));
    },

    setPhoneFieldValue(id, item, confirmation) {
      const phone = item ? [item.code || '', item.number || ''] : [];

      this.setFieldValue(id, patternize(phone.join(''), {
        format: CELLPHONE_FORMAT,
        bigFormat: PHONE_FORMAT,
      }), {
        silent: true,
      });

      if (confirmation && item && item.phone_confirmation && item.phone_confirmation.confirmed) {
        this.setFieldValidated(id, 'Confirmado');
        this.setFieldValue(confirmation, item.phone_confirmation.id);
      }
    },

    setAddressFieldsValue(map, address) {
      if ([undefined, null].indexOf(address) > -1) {
        return;
      }

      const {
        setValue,
      } = this;

      Object.keys(map)
        .filter(key => [
          'zip_code',
          'build_type_id',
          'set_time_year',
          'INTERNAL_present',
          'INTERNAL_address_separator',
        ].indexOf(key) === -1)
        .forEach(key => setValue(map[key], address[key]));

      setValue(map.zip_code, address.zip_code, { silent: true });
      setValue(map.state_id, address.state && address.state.id);

      if (map.INTERNAL_present) {
        setValue(map.INTERNAL_present, true);
        setValue(map.INTERNAL_address_separator, true);
      }

      if (map.build_type_id) {
        setValue(map.build_type_id, address.build_type && address.build_type.id);
      }

      if (map.set_time_year) {
        setValue(
          map.set_time_year,
          subtractDate(null, {
            years: address.set_time_year,
            months: address.set_time_month,
            format: 'YYYY-MM-DD',
          }),
        );
      }
    },

    getFieldOption(id) {
      const field = this.getField(id);
      const map = {
        value: '',
        label: '',
      };

      if (!field) {
        return map;
      }

      return [...field.options].find(o => o && o.value === field.value) || {
        value: '',
        label: '',
      };
    },
  },
};
